import {
  useHotkey,
  useMemoizedFn,
  useUpdateLayoutEffect,
} from "@seamapi/shared/react-hooks"
import { HugeiconsIcon } from "@hugeicons/react"
import { Cancel01Icon, Search01Icon } from "@hugeicons-pro/core-stroke-rounded"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@seamapi/ui/components/ui/dialog"
import { useCmdkFocus } from "core/components/Cmdk/Cmdk.hooks"
import { useCmdkControl, type CmdkItem } from "@seamapi/react-cmdk"
import { ComponentProps, ReactNode, useMemo, useRef } from "react"
import { cn } from "src/ui/utils"
import { create } from "zustand"
import { CmdkOption } from "core/components/Cmdk/CmdkOption"

type CmdkState = {
  visible: boolean
  setVisible: (visible: boolean) => void
  toggleVisible: () => void
}

export const useCmdkState = create<CmdkState>((set) => ({
  visible: false,
  setVisible: (visible: boolean) => set({ visible }),
  toggleVisible: () => set((state) => ({ visible: !state.visible })),
}))

/** One kind of thing the palette finds, shown before the user types. */
export interface CmdkSearchHint {
  icon: ReactNode
  label: string
  example: string
}

export function Cmdk({
  placeholder = "Search commands...",
  searchHints = [],
}: {
  placeholder?: string
  /** What typing searches, listed while the palette has nothing to show. */
  searchHints?: readonly CmdkSearchHint[]
} = {}) {
  const { visible, toggleVisible, setVisible } = useCmdkState()
  const control = useCmdkControl()
  const { page, closePage } = control
  const contentRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const renderInfo = useMemo(() => {
    let currentItemIdx = 0

    const list = Object.values(control.registry)
      .toSorted((a, b) => a.weight - b.weight)
      .flatMap((group) => {
        if (group.items.length === 0) {
          return []
        }

        const groupValue = {
          type: "group" as const,
          key: group.key,
          name: group.groupName,
        } as const

        const items = group.items.map((item) => {
          const itemValue = {
            type: "item" as const,
            index: currentItemIdx,
            value: item,
          } as const

          currentItemIdx += 1

          return itemValue
        })

        return [groupValue, ...items] as const
      })

    const items = list.filter((item) => item.type === "item")

    return {
      list,
      items,
    }
  }, [control.registry, visible])

  const { focusIndex, setFocusIndex } = useCmdkFocus({
    contentRef,
    inputRef,
  })

  const focusElementInDirection = useMemoizedFn(
    (direction: "next" | "prev") => {
      let nextIndex = direction === "next" ? focusIndex + 1 : focusIndex - 1

      if (nextIndex < 0) {
        nextIndex = renderInfo.items.length - 1
      } else if (nextIndex >= renderInfo.items.length) {
        nextIndex = 0
      }

      setFocusIndex(nextIndex, true)
    }
  )

  useHotkey(
    "cmd+k,ctrl+k",
    () => {
      toggleVisible()
    },
    { input: true }
  )

  useHotkey(
    "up",
    () => {
      focusElementInDirection("prev")
    },
    { input: true, getIsActive: () => visible }
  )

  useHotkey(
    "down",
    () => {
      focusElementInDirection("next")
    },
    { input: true, getIsActive: () => visible }
  )

  useUpdateLayoutEffect(() => {
    setFocusIndex(0, true)
  }, [renderInfo])

  useUpdateLayoutEffect(() => {
    if (!visible) {
      setFocusIndex(0)
      closePage()
    }
  }, [visible])

  const triggerAction = useMemoizedFn((item: CmdkItem) => {
    if (control.triggerItem(item)) {
      setVisible(false)
    }
  })

  useHotkey(
    "enter",
    () => {
      const item = renderInfo.items[focusIndex]
      if (item) {
        triggerAction(item.value)
      }
    },
    { input: true, getIsActive: () => visible }
  )

  const listboxId = "cmdk-listbox"
  const focusedItem = renderInfo.items[focusIndex]
  const activeDescendantId = focusedItem
    ? `cmdk-option-${focusedItem.value.id}`
    : undefined

  return (
    <Dialog open={visible} onOpenChange={setVisible}>
      <DialogContent
        aria-label="Command Palette"
        className="top-[15vh] w-[calc(100vw-2rem)] max-w-[680px] translate-y-0 gap-0 overflow-hidden rounded-xl border-border p-0 text-popover-foreground [background:linear-gradient(var(--popover),var(--popover)),var(--background)] shadow-2xl sm:rounded-xl"
        hideCloseButton
        onEscapeKeyDown={(e) => {
          // Escape steps back out of a sub-page before closing the palette.
          if (page) {
            e.preventDefault()
            closePage()
          }
        }}
      >
        <DialogTitle className="sr-only">Command Menu</DialogTitle>

        <div className="flex items-center gap-3 border-b px-4">
          {page ? (
            <button
              type="button"
              onClick={closePage}
              className="shrink-0 rounded-md bg-muted px-2 py-1 text-xs text-muted-foreground hover:text-foreground"
            >
              ← {page.title}
            </button>
          ) : (
            <HugeiconsIcon
              icon={Search01Icon}
              strokeWidth={2}
              className="size-5 shrink-0 text-muted-foreground"
            />
          )}
          <input
            {...control.getInputProps()}
            ref={inputRef}
            autoFocus
            placeholder={page?.placeholder ?? placeholder}
            role="combobox"
            aria-expanded={visible}
            aria-haspopup="listbox"
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeDescendantId}
            className="h-14 min-w-0 flex-1 bg-transparent text-lg text-foreground outline-none placeholder:text-muted-foreground"
            onKeyDown={(e) => {
              if (
                e.key === "Backspace" &&
                page &&
                e.currentTarget.value === ""
              ) {
                e.preventDefault()
                closePage()
                return
              }

              if (e.key === "Tab") {
                e.preventDefault()
                e.stopPropagation()
                focusElementInDirection(e.shiftKey ? "prev" : "next")
              }
            }}
          />
          {control.getQuery() !== "" && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                control.setQuery("")
                inputRef.current?.focus()
              }}
              className="flex size-7 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <HugeiconsIcon
                icon={Cancel01Icon}
                strokeWidth={2}
                className="size-4"
              />
            </button>
          )}
        </div>

        <div
          ref={contentRef}
          id={listboxId}
          role="listbox"
          aria-label="Commands"
          className="relative max-h-[min(440px,60vh)] overflow-y-auto p-2 [&>*]:scroll-mt-[36px]"
        >
          {renderInfo.list.length === 0 && (
            <CmdkEmptyState
              query={control.getQuery()}
              searchHints={page == null ? searchHints : []}
            />
          )}
          {renderInfo.list.map((item) =>
            item.type === "group" ? (
              <CmdkGroupHeader key={`group-${item.key}`}>
                {item.name}
              </CmdkGroupHeader>
            ) : (
              <CmdkOption
                key={item.value.id}
                index={item.index}
                item={item.value}
                focused={focusIndex === item.index}
                onSelect={triggerAction}
                onSetFocusIndex={setFocusIndex}
              />
            )
          )}
        </div>

        <div className="flex items-center justify-end gap-4 border-t bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
          <KeyHint keys={["↑", "↓"]} label="Move" />
          <KeyHint keys={["↵"]} label="Open" />
          <KeyHint keys={["esc"]} label={page ? "Back" : "Close"} />
        </div>
      </DialogContent>
    </Dialog>
  )
}

/** What the palette shows with nothing to list. */
function CmdkEmptyState({
  query,
  searchHints,
}: {
  query: string
  searchHints: readonly CmdkSearchHint[]
}) {
  if (query !== "" || searchHints.length === 0) {
    return (
      <p className="px-2 py-10 text-center text-sm text-muted-foreground">
        {query === "" ? "Type to search." : `No results for “${query}”`}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-1 px-1 py-2">
      <p className="px-2 pb-1 text-xs font-medium text-muted-foreground">
        Type anything to search
      </p>
      {searchHints.map((hint) => (
        <div
          key={hint.label}
          className="flex h-11 items-center gap-3 px-2 text-[15px] text-foreground"
        >
          <span className="inline-grid size-5 shrink-0 place-items-center [&_path]:text-muted-foreground [&_svg]:size-[18px]">
            {hint.icon}
          </span>
          <span className="shrink-0">{hint.label}</span>
          <span className="truncate text-sm text-muted-foreground">
            {hint.example}
          </span>
        </div>
      ))}
    </div>
  )
}

function KeyHint({ keys, label }: { keys: string[]; label: string }) {
  return (
    <span className="flex items-center gap-1">
      {keys.map((key) => (
        <kbd
          key={key}
          className="min-w-5 rounded border bg-background px-1.5 py-0.5 text-center font-sans text-[11px] leading-none"
        >
          {key}
        </kbd>
      ))}
      {label}
    </span>
  )
}

function CmdkGroupHeader(props: ComponentProps<"h2">) {
  const { children, className, ...attrs } = props

  return (
    <h2
      aria-hidden="true"
      className={cn(
        "[background:linear-gradient(var(--popover),var(--popover)),var(--background)] sticky top-0 w-full text-muted-foreground text-xs font-medium shrink-0 grow-0 h-[36px] flex flex-row items-center px-3",
        className
      )}
      {...attrs}
    >
      {children}
    </h2>
  )
}
